/**
 * Somewhere to sign.
 *
 * Pointer events rather than mouse or touch events, so a finger, a stylus and
 * a mouse all take the same path through the code — and so a tablet does not
 * fire a synthetic click after every stroke.
 *
 * The output is trimmed to the ink before it leaves here. A signature drawn in
 * the corner of a wide canvas, saved whole, ends up scaled down to nothing
 * when it is fitted into a box on the form; trimming first means what lands on
 * the paper is the size of what was written.
 */

import { useEffect, useRef, useState } from "react";

const PEN = "#10203a";

export interface SignaturePadHandle {
  clear: () => void;
  isEmpty: () => boolean;
  toPng: () => string | null;
}

export default function SignaturePad({
  label, onChange, height = 180, disabled = false,
}: {
  label: string;
  /** Fires with the trimmed PNG, or null when cleared. */
  onChange: (png: string | null) => void;
  height?: number;
  disabled?: boolean;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawing = useRef(false);
  const last = useRef<{ x: number; y: number } | null>(null);
  const inked = useRef(false);
  const [empty, setEmpty] = useState(true);

  /* Backing store at device resolution, CSS size in layout pixels. Without
     this a signature on a phone is drawn at a third of the screen's real
     resolution and prints as a blurry line. */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const setup = () => {
      const ratio = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      if (rect.width === 0) return;
      // Preserve anything already drawn across a resize (an orientation
      // change mid-signature should not wipe the name).
      const previous = inked.current ? canvas.toDataURL("image/png") : null;

      canvas.width = Math.round(rect.width * ratio);
      canvas.height = Math.round(rect.height * ratio);
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.scale(ratio, ratio);
      ctx.lineWidth = 2.4;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.strokeStyle = PEN;

      if (previous) {
        const img = new Image();
        img.onload = () => ctx.drawImage(img, 0, 0, rect.width, rect.height);
        img.src = previous;
      }
    };

    setup();
    const observer = new ResizeObserver(setup);
    observer.observe(canvas);
    return () => observer.disconnect();
  }, []);

  const pointAt = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const start = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (disabled) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    drawing.current = true;
    last.current = pointAt(e);
  };

  const move = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current || disabled) return;
    const ctx = canvasRef.current?.getContext("2d");
    const from = last.current;
    if (!ctx || !from) return;
    const to = pointAt(e);

    ctx.beginPath();
    ctx.moveTo(from.x, from.y);
    ctx.lineTo(to.x, to.y);
    ctx.stroke();

    last.current = to;
    if (!inked.current) { inked.current = true; setEmpty(false); }
  };

  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    last.current = null;
    if (inked.current) onChange(trimmed(canvasRef.current));
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.restore();
    inked.current = false;
    setEmpty(true);
    onChange(null);
  };

  return (
    <div className={`sigpad ${disabled ? "off" : ""}`}>
      <div className="sigpad-head">
        <label>{label}</label>
        {!empty && (
          <button type="button" className="linkbtn tiny" onClick={clear}>clear</button>
        )}
      </div>
      <div className="sigpad-box" style={{ height }}>
        <canvas
          ref={canvasRef}
          onPointerDown={start}
          onPointerMove={move}
          onPointerUp={end}
          onPointerCancel={end}
          onPointerLeave={end}
        />
        <span className="sigpad-rule" aria-hidden="true" />
        {empty && <span className="sigpad-hint">Sign here</span>}
      </div>
    </div>
  );
}

/**
 * Crop to the ink and return a PNG.
 *
 * Scans the alpha channel for the drawn bounds, then redraws that region on a
 * transparent canvas with a small margin. Returns null if nothing was drawn.
 */
export function trimmed(canvas: HTMLCanvasElement | null): string | null {
  if (!canvas) return null;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;

  const { width, height } = canvas;
  if (width === 0 || height === 0) return null;

  const { data } = ctx.getImageData(0, 0, width, height);
  let top = height, left = width, right = -1, bottom = -1;

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      // Alpha only: the pen is the sole thing ever drawn here.
      if (data[(y * width + x) * 4 + 3]! > 8) {
        if (y < top) top = y;
        if (y > bottom) bottom = y;
        if (x < left) left = x;
        if (x > right) right = x;
      }
    }
  }
  if (right < 0) return null;

  const margin = Math.round(Math.min(width, height) * 0.02);
  const x0 = Math.max(0, left - margin);
  const y0 = Math.max(0, top - margin);
  const w = Math.min(width, right + margin) - x0;
  const h = Math.min(height, bottom + margin) - y0;
  if (w <= 0 || h <= 0) return null;

  const out = document.createElement("canvas");
  out.width = w;
  out.height = h;
  const octx = out.getContext("2d");
  if (!octx) return null;
  octx.drawImage(canvas, x0, y0, w, h, 0, 0, w, h);
  return out.toDataURL("image/png");
}
